import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { HOTEL, inr } from "@/constants/hotelData";

const LINKS = [
  { label: "Rooms", href: "#rooms" },
  { label: "Amenities", href: "#amenities" },
  { label: "Gallery", href: "#gallery" },
  { label: "Location", href: "#location" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToSection(href), 60);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" onClick={() => setOpen(false)}>
          <Logo />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-[11px] font-medium uppercase tracking-[0.22em] text-cream/80 transition-colors hover:text-gold"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          <span className="rounded-full border border-gold/30 px-4 py-1.5 font-mono text-[11px] tracking-wider text-gold">
            {HOTEL.totalRooms} Rooms · {inr(HOTEL.ratePerNight)}/night
          </span>
          <button
            data-testid="nav-book-button"
            onClick={() => navigate("/book")}
            className="rounded-full bg-gold px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_28px_rgba(212,175,55,0.4)]"
          >
            Book Stay
          </button>
        </div>
        <button
          data-testid="nav-menu-toggle"
          className="text-cream lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="glass mx-4 mt-3 rounded-2xl p-6 lg:hidden"
          >
            <div className="flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left text-xs font-medium uppercase tracking-[0.22em] text-cream/85 hover:text-gold"
                >
                  {l.label}
                </button>
              ))}
              <div className="h-px bg-gold/20" />
              <button
                data-testid="nav-mobile-book-button"
                onClick={() => {
                  setOpen(false);
                  navigate("/book");
                }}
                className="rounded-full bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink"
              >
                Book Your Stay — {inr(HOTEL.ratePerNight)}/night
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
